import { FirebasePaymentMethodsType } from '@monofront/types';
import { useEffect, useState } from 'react';
import { PAYMENT_PREFERENCES_DOCUMENT_NAME } from '../constants';
import { IAuthState } from '../contexts';
import { IUserPreferences, UserPreferencesSubDocument } from '../interfaces';

/**
 * Factory function that build a usePaymentPreferences hook
 * This hook enables to know if the modal expiring card has to be displayed or not
 * @param useAuthentication the hook that handles the authentication
 * @param createOrGetPreferences service that handles the user preferences
 * @returns a hook which enables to know if the modal expiring card has to be displayed or not
 */
export const createUsePaymentPreferences = (
  useAuthentication: () => Partial<IAuthState>,
  createOrGetPreferences: <K extends keyof IUserPreferences>(
    customerUid: string,
    paymentPreferencesDocumentName: K,
  ) => Promise<UserPreferencesSubDocument[K]>,
) => {
  return () => {
    const { customer } = useAuthentication();
    const [isExpiringCardModalOpen, setIsExpiringCardModalOpen] =
      useState(false);

    useEffect(() => {
      const checkPaymentMethodDate = async () => {
        const paymentPreferences = await createOrGetPreferences(
          customer!.uid,
          PAYMENT_PREFERENCES_DOCUMENT_NAME,
        );

        if (
          !paymentPreferences?.doNotAskAgainUpdateSubPM &&
          customer?.paymentMethod &&
          customer?.paymentMethod.type === FirebasePaymentMethodsType.Card
        ) {
          const willExpire =
            new Date(
              customer?.paymentMethod.exp_year,
              customer?.paymentMethod.exp_month - 1,
            ).getTime() -
              new Date().getTime() <
            0;

          setIsExpiringCardModalOpen(willExpire);
        }
      };

      customer && checkPaymentMethodDate();
    }, [customer]);

    return { isExpiringCardModalOpen, setIsExpiringCardModalOpen };
  };
};
